import { LoadingOutlined } from '@ant-design/icons';
import { Button, Input, Spin } from 'antd';
import { Content } from 'antd/lib/layout/layout';
import cx from 'classnames';
import moment from 'moment';
import React, { Component, ReactNode } from 'react';
import { connect } from 'react-redux';
import { Redirect, RouteComponentProps, withRouter } from 'react-router';

import { BlockedByFeature } from '../../../featureToggles/features/utils';
import { IAccount } from '../../../slices/accounts/account';
import { fetchAccountById } from '../../../slices/accounts/accounts.thunks';
import { selectors as accountListSelectors } from '../../../slices/accounts/list';
import {
  ContactInfoUpdateData,
  updateAccountBillingContact
} from '../../../slices/accounts/updateAccountBillingContact/updateAccountBillingContact.thunks';
import { selectors as authSelectors } from '../../../slices/auth';
import { ISubscription } from '../../../slices/customer/customer';
import { fetchCustomerSubscription } from '../../../slices/customer/customer.thunk';
import { RootState } from '../../../slices/rootReducer';
import { IUser } from '../../../slices/users/user';
import { PaymentOptions } from '../../accounts/modal/paymentAndBilling/types';
import { CadenceTypes } from '../../auth/proRegister/cadenceSelector';
import { CadenceTypesValues } from '../../auth/proRegister/subscriptionTypes';
import { LinkButton } from '../../common/linkButton';
import { UpdatePaymentModal } from '../updatePaymentModal';

import styles from './billingSettings.module.scss';

interface State {
  editingContactInfo: boolean;
  editingPaymentMethod: boolean;
  contactFirstName: string;
  contactLastName: string;
  contactEmail: string;
  subscriptionCharge: string;
  billingCadence: string;
  nextPaymentDate: string;
  paymentMethod: string;
  cardLastFourDigits: string;
}

interface StateProps {
  account?: IAccount | typeof BlockedByFeature | null;
  accountId: string;
  selectedAccount: IAccount | null;
  subscription: ISubscription | null;
  isUpdatingBillingContact: boolean;
  isUpdatingPaymentMethod: boolean;
  loggedUser: IUser | null;
  isLiteAccount: boolean;
  isLoggedUserOwnerOrAdmin: boolean;
  isLoggedUserSxAdmin: boolean;
  loadingSubscription: boolean;
  errorUpdatingPaymentMethod: boolean;
  cannotAccessThisPage?: boolean;
}

interface DispatchProps {
  fetchCustomerSubscription: (customerId: string) => void;
  fetchAccountById: (accountId: string) => void;
  updateAccountBillingContact: (data: ContactInfoUpdateData) => void;
}

interface MatchProps {
  id: string;
}

type ComponentProps = RouteComponentProps<MatchProps> & StateProps & DispatchProps;

type IPaymentOptionsLabels = {
  [key in PaymentOptions]: string;
};

const paymentOptionLabels: IPaymentOptionsLabels = {
  [PaymentOptions.CreditCard]: 'Credit card',
  [PaymentOptions.Invoice]: 'Invoice',
  [PaymentOptions.ServiceProject]: 'Service project',
  [PaymentOptions.Bulk]: 'Bulk'
};

class BillingSettingDashboardBase extends Component<ComponentProps, State> {
  constructor(props: ComponentProps) {
    super(props);
    this.state = {
      editingContactInfo: false,
      editingPaymentMethod: false,
      contactFirstName: '',
      contactLastName: '',
      contactEmail: '',
      subscriptionCharge: '',
      billingCadence: '',
      nextPaymentDate: '',
      paymentMethod: '',
      cardLastFourDigits: ''
    };
    this.enableEditContact = this.enableEditContact.bind(this);
    this.disableEditContact = this.disableEditContact.bind(this);
    this.renderContactName = this.renderContactName.bind(this);
    this.renderContactEmail = this.renderContactEmail.bind(this);
    this.renderEditContactActions = this.renderEditContactActions.bind(this);
    this.onFirstNameChange = this.onFirstNameChange.bind(this);
    this.onLastNameChange = this.onLastNameChange.bind(this);
    this.onEmailChange = this.onEmailChange.bind(this);
    this.renderPaymentMethod = this.renderPaymentMethod.bind(this);
    this.renderLastFourDigits = this.renderLastFourDigits.bind(this);
    this.renderBillingCadence = this.renderBillingCadence.bind(this);
    this.renderRenewalDate = this.renderRenewalDate.bind(this);
    this.renderSubscriptionCharge = this.renderSubscriptionCharge.bind(this);
    this.editContact = this.editContact.bind(this);
    this.handleOpenModal = this.handleOpenModal.bind(this);
  }

  componentDidMount() {
    if (this.props.cannotAccessThisPage) {
      return;
    }
    const { accountId, fetchAccountById, fetchCustomerSubscription } = this.props;
    if (accountId) {
      fetchCustomerSubscription(accountId);
      fetchAccountById(accountId);
    }
  }

  componentDidUpdate(prevProps: Readonly<ComponentProps>, prevState: Readonly<State>) {
    const selectedAccount = this.props.selectedAccount;
    const billingAddress = selectedAccount?.billingAddress;
    if (billingAddress && billingAddress.email !== prevProps.selectedAccount?.billingAddress?.email) {
      this.setState({
        contactFirstName: billingAddress.firstName || '',
        contactLastName: billingAddress.lastName || '',
        contactEmail: billingAddress.email || '',
        nextPaymentDate: selectedAccount?.renewalAt?.toString() || '',
        paymentMethod: paymentOptionLabels[selectedAccount?.paymentOptions[0] as PaymentOptions]
      });
    }
    const subscription = this.props.subscription;
    if (subscription && subscription.id !== prevProps.subscription?.id) {
      const cadenceIndex = Object.keys(CadenceTypes).indexOf(subscription.paymentCadence);
      this.setState({
        subscriptionCharge: `${(subscription.amount / 100).toString()} per ${subscription.paymentCadence === CadenceTypesValues.MONTHLY ? 'month' : 'year'}`,
        billingCadence: Object.values(CadenceTypes)[cadenceIndex] || '',
        cardLastFourDigits: subscription.card?.lastFourDigits || ''
      });
    }

    if (!this.props.isUpdatingBillingContact && this.props.isUpdatingBillingContact !== prevProps.isUpdatingBillingContact) {
      this.setState({
        editingContactInfo: false
      });
    }
    if (this.state.editingPaymentMethod && this.props.isUpdatingPaymentMethod !== prevProps.isUpdatingPaymentMethod && !this.props.isUpdatingPaymentMethod && !this.props.errorUpdatingPaymentMethod) {
      this.props.fetchCustomerSubscription(this.props.accountId);
      this.setState({
        editingPaymentMethod: false
      });
    }
  }

  private handleOpenModal() {
    this.setState({
      editingPaymentMethod: !this.state.editingPaymentMethod
    });
  }

  private onFirstNameChange(e: React.ChangeEvent<HTMLInputElement>) {
    this.setState({
      contactFirstName: e.target?.value
    });
  }

  private onLastNameChange(e: React.ChangeEvent<HTMLInputElement>) {
    this.setState({
      contactLastName: e.target?.value
    });
  }

  private onEmailChange(e: React.ChangeEvent<HTMLInputElement>) {
    this.setState({
      contactEmail: e.target?.value
    });
  }

  private renderContactName() {
    const { editingContactInfo, contactFirstName, contactLastName } = this.state;
    if (editingContactInfo) {
      return <div className={styles.nameInputs}>
        <Input
          placeholder="First name"
          className={styles.input}
          value={contactFirstName}
          onChange={this.onFirstNameChange}
        />
        <Input
          placeholder="Last name"
          className={styles.input}
          value={contactLastName}
          onChange={this.onLastNameChange}
        />
      </div>;
    }
    return `${contactFirstName} ${contactLastName}`;
  }

  private renderContactEmail() {
    const { editingContactInfo, contactEmail } = this.state;
    if (editingContactInfo) {
      return <Input
        placeholder="Email"
        className={styles.input}
        value={contactEmail}
        onChange={this.onEmailChange}
      />;
    }
    return contactEmail;
  }

  private enableEditContact() {
    this.setState({
      editingContactInfo: true
    });
  }

  private disableEditContact() {
    const selectedAccount = this.props.selectedAccount;
    const billingAddress = selectedAccount?.billingAddress;
    this.setState({
      editingContactInfo: false,
      contactFirstName: billingAddress?.firstName || '',
      contactLastName: billingAddress?.lastName || '',
      contactEmail: billingAddress?.email || ''
    });
  }

  private editContact() {
    const { accountId, updateAccountBillingContact } = this.props;
    updateAccountBillingContact({
      accountId,
      firstName: this.state.contactFirstName,
      lastName: this.state.contactLastName,
      email: this.state.contactEmail
    });
  }

  private renderEditContactActions() {
    const { editingContactInfo, contactEmail } = this.state;
    const { isUpdatingBillingContact } = this.props;
    if (editingContactInfo) {
      return <div className={styles.actionButtons}>
        <Button type="default" disabled={isUpdatingBillingContact} onClick={this.disableEditContact}>Discard</Button>
        <Button type="primary" disabled={isUpdatingBillingContact} loading={isUpdatingBillingContact} onClick={this.editContact}>{!isUpdatingBillingContact && 'Save'}</Button>
      </div>;
    }
    return contactEmail && <LinkButton onClick={this.enableEditContact}>Edit</LinkButton>;
  }

  private renderPaymentMethod() {
    return this.state.paymentMethod;
  }

  private renderBillingCadence() {
    return this.state.billingCadence;
  }

  private renderSubscriptionCharge() {
    if (!this.state.subscriptionCharge) {
      return null;
    }
    return `$${this.state.subscriptionCharge}`;
  }

  private renderRenewalDate() {
    if (!this.state.nextPaymentDate) {
      return null;
    }
    return `Next payment date: ${moment(this.state.nextPaymentDate).format('MMMM D, YYYY')}`;
  }

  private renderLastFourDigits() {
    if (!this.state.cardLastFourDigits) {
      return null;
    }
    return `**** **** **** ${this.state.cardLastFourDigits}`;
  }

  private renderLoading() {
    return <Content className={styles.dashboardContent}>
      <div className={cx(styles.mainContent, styles.loading)}>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 44 }} spin />} />
      </div>
    </Content>;
  }

  private renderContactNameRow() {
    const { editingContactInfo, contactFirstName, contactLastName, contactEmail } = this.state;
    const { isUpdatingBillingContact } = this.props;
    if (editingContactInfo) {
      return <>
        <div className={cx(styles.editLabelCol, styles.title)}>Billing contact</div>
        <div className={styles.editNameCol}>
          <div className={styles.nameInputs}>
            <Input
              placeholder="First name"
              className={styles.input}
              value={contactFirstName}
              onChange={this.onFirstNameChange}
            />
            <Input
              placeholder="Last name"
              className={styles.input}
              value={contactLastName}
              onChange={this.onLastNameChange}
            />
          </div>
        </div>
        <div className={styles.editEmailCol}>
          <Input
            placeholder="Email"
            className={styles.input}
            value={contactEmail}
            onChange={this.onEmailChange}
          />
        </div>
        <div className={cx(styles.editActionsCol)}>
          <div className={styles.actionButtons}>
            <Button type="default" disabled={isUpdatingBillingContact}
              onClick={this.disableEditContact}>Discard</Button>
            <Button type="primary" disabled={isUpdatingBillingContact} loading={isUpdatingBillingContact}
              onClick={this.editContact}>{!isUpdatingBillingContact && 'Save'}</Button>
          </div>
        </div>
      </>;
    }
    return <>
      <div className={cx(styles.col, styles.title)}>Billing contact</div>
      <div className={styles.col}>{contactFirstName} {contactLastName}</div>
      <div className={styles.col}>{contactEmail}</div>
      <div className={cx(styles.col, styles.action)}>
        {contactEmail && <LinkButton onClick={this.enableEditContact}>Edit</LinkButton>}
      </div>
    </>;
  }

  render(): ReactNode {
    if (this.props.cannotAccessThisPage) {
      return <Redirect to="/settings/profile" />;
    }
    const { editingPaymentMethod } = this.state;
    const { isLiteAccount, isLoggedUserOwnerOrAdmin, isLoggedUserSxAdmin, account, loadingSubscription } = this.props;

    if (isLiteAccount || (!isLoggedUserOwnerOrAdmin && !isLoggedUserSxAdmin)) {
      return <Redirect to="/settings/profile" />;
    }

    if (!account || loadingSubscription) {
      return this.renderLoading();
    }

    return (
      <Content className={styles.dashboardContent}>
        <div className={styles.mainContent}>
          <div className={styles.listItem}>
            <div className={cx(styles.col, styles.title)}>Subscription charge</div>
            <div className={styles.col}>{this.renderSubscriptionCharge()}</div>
            <div className={styles.col} />
            <div className={cx(styles.col, styles.action)} />
          </div>
          <div className={styles.listItem}>
            <div className={cx(styles.col, styles.title)}>Billing cadence</div>
            <div className={styles.col}>{this.renderBillingCadence()}</div>
            <div className={styles.col}>{this.renderRenewalDate()}</div>
            <div className={cx(styles.col, styles.action)} />
          </div>
          <div className={styles.listItem}>
            <div className={cx(styles.col, styles.title)}>Payment method</div>
            <div className={styles.col}>{this.renderPaymentMethod()}</div>
            <div className={styles.col}>{this.renderLastFourDigits()}</div>
            <div className={cx(styles.col, styles.action)}>
              {this.state.cardLastFourDigits && <LinkButton onClick={this.handleOpenModal}>Edit</LinkButton>}
            </div>
          </div>
          <div className={styles.listItem}>
            {this.renderContactNameRow()}
          </div>
        </div>
        {
          editingPaymentMethod &&
          <UpdatePaymentModal
            isOpen={editingPaymentMethod}
            onCancel={this.handleOpenModal}
          />
        }
      </Content>
    );
  }
}

const mapStateToProps = (state: RootState): StateProps => {
  const {
    accounts: {
      selectedAccount: { account = {} } = {},
      updateBillingContact,
      paymentMethod
    },
    subscription: { data = null, loading: loadingSubscription },
    auth: { user }
  } = state;

  const loggedUser = authSelectors.getLoggedInUser(state);
  const isEnterpriseAccount = accountListSelectors.isEnterpriseAccount(state, loggedUser?.accountId ?? '');
  const isUserAdmin = authSelectors.isAuthUserAdminOrOwner(state);
  const isUserSxAdmin = authSelectors.isAuthUserSightxAdmin(state);
  const accountId = user?.accountId ?? '';
  return {
    account: accountListSelectors.getAccountById(state, accountId),
    accountId,
    selectedAccount: account as IAccount,
    subscription: data,
    loadingSubscription,
    isUpdatingBillingContact: updateBillingContact.loading,
    loggedUser,
    isUpdatingPaymentMethod: paymentMethod.loading,
    errorUpdatingPaymentMethod: paymentMethod.error,
    isLiteAccount: accountListSelectors.isLiteAccount(state, accountId),
    isLoggedUserOwnerOrAdmin: authSelectors.isAuthUserAdminOrOwner(state),
    isLoggedUserSxAdmin: authSelectors.isAuthUserSightxAdmin(state),
    cannotAccessThisPage: isEnterpriseAccount && !isUserAdmin && !isUserSxAdmin
  };
};

const dispatchToProps: DispatchProps = {
  fetchCustomerSubscription,
  fetchAccountById,
  updateAccountBillingContact
};

const ConnectedBillingDashboard = connect(mapStateToProps, dispatchToProps)(BillingSettingDashboardBase);

export const BillingSettingsDashboard = withRouter(ConnectedBillingDashboard);
